import { useEffect, useRef, useState } from "react";
import { http } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  Megaphone, X, Loader2, Camera, MapPin, ImagePlus,
  Cloud, Landmark, Leaf, HandHelping, Feather, Users, MessageSquare,
} from "lucide-react";

// -----------------------------------------------------------------------------
// QuickReporter — a Facebook-style "📢 Report" button that lives on every
// page. On mobile it's a floating pill at the bottom-right. On desktop it
// also shows as a violet pill in the header. Tap opens a modal to file a
// weather / civic / nature / obituary / community post in seconds:
//   • Pick the report kind (icon-driven, one tap)
//   • Type a short title + description
//   • Attach up to 6 photos straight from the phone camera or gallery
//   • Optionally tag the current geolocation
// Submission goes to POST /api/quick-reports → moderation queue → feed.
// Only signed-in users see the trigger. Guests get a soft sign-in nudge.
// -----------------------------------------------------------------------------

const KINDS = [
  { id: "weather",     label: "Weather",    icon: Cloud,       tint: "#0EA5E9" },
  { id: "civic",       label: "Civic",      icon: Landmark,    tint: "#F59E0B" },
  { id: "nature",      label: "Nature",     icon: Leaf,        tint: "#10B981" },
  { id: "help_needed", label: "Need Help",  icon: HandHelping, tint: "#EF4444" },
  { id: "help_offered",label: "Offer Help", icon: HandHelping, tint: "#059669" },
  { id: "obituary",    label: "Obituary",   icon: Feather,     tint: "#7C3AED" },
  { id: "community",   label: "Community",  icon: Users,       tint: "#EC4899" },
  { id: "other",       label: "Other",      icon: MessageSquare, tint: "#64748B" },
];

const MAX_IMAGES = 6;

export function QuickReporterButton({ variant = "floating" }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  if (variant === "floating") {
    return (
      <>
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="quick-report-fab"
          className="fixed bottom-24 right-6 z-40 rounded-full shadow-lg text-white inline-flex items-center gap-2 px-4 py-3 md:px-5 md:py-3.5 text-sm font-medium transition-transform hover:scale-105"
          style={{
            background: "linear-gradient(90deg, #7C3AED 0%, #EC4899 100%)",
            boxShadow: "0 10px 25px -5px rgba(139, 92, 246, 0.45)",
          }}
          title="Report something"
          aria-label="Report something"
        >
          <Megaphone className="w-4 h-4" />
          <span className="hidden sm:inline">Report</span>
        </button>
        {open && <QuickReporterModal onClose={() => setOpen(false)} user={user} />}
      </>
    );
  }

  // Header variant — solid pill for desktop nav bars.
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="quick-report-header-btn"
        className="inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium text-white transition-transform hover:scale-[1.03]"
        style={{ background: "linear-gradient(90deg, #7C3AED 0%, #EC4899 100%)" }}
      >
        <Megaphone className="w-3.5 h-3.5" />
        Report
      </button>
      {open && <QuickReporterModal onClose={() => setOpen(false)} user={user} />}
    </>
  );
}


function QuickReporterModal({ onClose, user }) {
  const [kind, setKind] = useState("community");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]); // [{path, url, previewUrl}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [coords, setCoords] = useState(null);
  const [locationLabel, setLocationLabel] = useState("");
  const fileRef = useRef(null);

  // Block body scroll while open.
  useEffect(() => {
    const orig = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = orig; };
  }, []);

  const notSignedIn = !user?.id;

  const attachGeo = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation not available on this device.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude });
        setLocationLabel(`${pos.coords.latitude.toFixed(4)}, ${pos.coords.longitude.toFixed(4)}`);
        toast.success("Location attached.");
      },
      (err) => toast.error("Could not read location: " + err.message),
      { timeout: 8000 },
    );
  };

  const pickFiles = () => fileRef.current?.click();

  const onFilesPicked = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      toast.error(`You can attach up to ${MAX_IMAGES} photos.`);
      e.target.value = "";
      return;
    }
    const toUpload = files.slice(0, remaining);
    setUploading(true);
    try {
      for (const f of toUpload) {
        const form = new FormData();
        form.append("file", f);
        try {
          const { data } = await http.post("/quick-reports/upload-image", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          setImages((prev) => [
            ...prev,
            { path: data.path, url: data.url, previewUrl: URL.createObjectURL(f) },
          ]);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Upload failed for one photo.");
        }
      }
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (idx) => {
    setImages((prev) => {
      const copy = [...prev];
      const removed = copy.splice(idx, 1)[0];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return copy;
    });
  };

  const submit = async () => {
    if (notSignedIn) {
      toast.error("Please sign in to file a report.");
      return;
    }
    if (title.trim().length < 3) {
      toast.error("Add a short title (at least 3 characters).");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        kind,
        title: title.trim(),
        description: description.trim(),
        image_paths: images.map((i) => i.path),
        latitude: coords?.lat,
        longitude: coords?.lng,
        location: locationLabel ? { display: locationLabel } : undefined,
      };
      await http.post("/quick-reports", payload);
      toast.success("Report submitted — it's in the moderation queue.");
      // Clean up preview URLs
      images.forEach((i) => i.previewUrl && URL.revokeObjectURL(i.previewUrl));
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="quick-report-modal"
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(15, 15, 35, 0.6)" }}
    >
      <div
        className="w-full sm:max-w-lg rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[95vh] overflow-y-auto"
        style={{ background: "var(--wofy-bg, #fff)" }}
      >
        {/* Header */}
        <div className="p-5 flex items-center justify-between border-b" style={{ borderColor: "var(--wofy-border)" }}>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">Report anything</div>
            <div className="font-display text-xl mt-0.5">What do you want to tell your community?</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="quick-report-close"
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-black/[0.05]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {notSignedIn && (
            <div
              className="rounded-xl border p-3 text-xs"
              style={{ borderColor: "rgba(139, 92, 246, 0.35)", background: "rgba(139, 92, 246, 0.06)", color: "#5B21B6" }}
            >
              Please sign in first — quick reports are attached to your account so your community can trust the source.
            </div>
          )}

          {/* Kind picker */}
          <div>
            <div className="text-xs opacity-70 mb-2">What kind of report is this?</div>
            <div className="grid grid-cols-4 gap-2" data-testid="quick-report-kind-picker">
              {KINDS.map((k) => {
                const Icon = k.icon;
                const active = kind === k.id;
                return (
                  <button
                    key={k.id}
                    type="button"
                    onClick={() => setKind(k.id)}
                    data-testid={`quick-report-kind-${k.id}`}
                    className="flex flex-col items-center gap-1 rounded-xl py-3 text-[11px] transition-transform hover:scale-[1.03]"
                    style={{
                      background: active ? `${k.tint}18` : "var(--wofy-surface)",
                      border: active ? `1.5px solid ${k.tint}` : "1px solid var(--wofy-border)",
                      color: active ? k.tint : "var(--wofy-text)",
                    }}
                  >
                    <Icon className="w-4 h-4" />
                    {k.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="text-xs opacity-70 block mb-1">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Short and to the point — e.g. 'Waterlogged near Poonjar market'"
              maxLength={140}
              data-testid="quick-report-title"
              className="w-full rounded-xl border px-3 py-2 text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
            />
          </div>

          {/* Description */}
          <div>
            <label className="text-xs opacity-70 block mb-1">Description (optional)</label>
            <textarea
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Anything the community should know? Time, road, whom to call…"
              maxLength={4000}
              data-testid="quick-report-description"
              className="w-full rounded-xl border px-3 py-2 text-sm bg-transparent resize-y"
              style={{ borderColor: "var(--wofy-border)" }}
            />
          </div>

          {/* Images */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs opacity-70">Photos ({images.length}/{MAX_IMAGES})</label>
              <button
                type="button"
                onClick={pickFiles}
                disabled={images.length >= MAX_IMAGES || uploading}
                data-testid="quick-report-add-photo"
                className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full"
                style={{
                  background: "rgba(139, 92, 246, 0.1)",
                  color: "#7C3AED",
                  border: "1px solid rgba(139, 92, 246, 0.3)",
                  opacity: images.length >= MAX_IMAGES ? 0.55 : 1,
                }}
              >
                {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImagePlus className="w-3.5 h-3.5" />}
                Add photo
              </button>
              <input
                type="file"
                ref={fileRef}
                accept="image/*"
                capture="environment"
                multiple
                onChange={onFilesPicked}
                data-testid="quick-report-file-input"
                className="hidden"
              />
            </div>
            {images.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {images.map((img, i) => (
                  <div key={i} className="relative aspect-square rounded-xl overflow-hidden border" style={{ borderColor: "var(--wofy-border)" }}>
                    <img src={img.previewUrl} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeImage(i)}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                      aria-label="Remove photo"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <button
              type="button"
              onClick={attachGeo}
              data-testid="quick-report-attach-location"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border"
              style={{ borderColor: "var(--wofy-border)" }}
            >
              <MapPin className="w-3.5 h-3.5" />
              {locationLabel ? locationLabel : "Attach my location"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t flex items-center justify-end gap-2 sticky bottom-0" style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-bg, #fff)" }}>
          <button
            type="button"
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-full hover:bg-black/[0.05]"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || uploading || title.trim().length < 3 || notSignedIn}
            data-testid="quick-report-submit"
            className="inline-flex items-center gap-2 text-sm font-medium text-white rounded-full px-5 py-2.5"
            style={{
              background: "linear-gradient(90deg, #7C3AED 0%, #EC4899 100%)",
              opacity: (submitting || uploading || title.trim().length < 3 || notSignedIn) ? 0.55 : 1,
            }}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Megaphone className="w-4 h-4" />}
            Publish report
          </button>
        </div>
      </div>
    </div>
  );
}

export default QuickReporterButton;
